import * as Blockly from 'blockly/core'
import { EventEmitter } from 'events'
import DEFAULT_TOOLBOX from './toolbox.json'
// @ts-expect-error Types are not available for this package.
import { ContinuousFlyout } from '../components/blockly/continuousToolbox/ContinuousFlyout.js'
import { generateCode } from './blocks.js'
import { DISABLED_REASONS, disableInvalidBlocks } from './utils.js'
import { Chapter } from '../openapi/api.js'
import {
  missionObjectExtensionName,
  missionObjectFieldName,
  missionObjectOrThisFieldName,
} from './constants'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deepEquals = function (a: any, b: any) {
  if (typeof a !== typeof b) {
    return false
  }

  if (typeof a === 'object' && a !== null && b != null) {
    if (Object.keys(a).length !== Object.keys(b).length) {
      return false
    }

    return Object.keys(a).every((k): boolean => {
      if (!Object.prototype.hasOwnProperty.call(b, k)) {
        return false
      }

      return deepEquals(a[k], b[k])
    })
  } else {
    return a === b
  }
}

type ToolboxType = Blockly.utils.toolbox.ToolboxDefinition

export interface MissionChapter {
  chapter?: Chapter
  toolbox?: ToolboxType | null
}

export interface MissionChapterSpec {
  chapter?: Chapter
  toolbox?: ToolboxType | string | null
}

export interface MissionObject {
  code?: string
  name: string
  sound?: boolean
  thumbnail?: string
  workspace?: Record<string, unknown>
}

export interface MissionData {
  chapters: Array<MissionChapterSpec>
  number: number
  objects: Array<MissionObject>
  stage: number
  template: string
  thumbnail: string
  title: string
  toolbox?: ToolboxType | string | null
}

const parseToolbox = (
  toolbox: ToolboxType | string | null,
): ToolboxType | null => {
  try {
    return (
      !toolbox ? null
      : typeof toolbox === 'string' ? JSON.parse(toolbox)
      : toolbox
    )
  } catch {
    return null
  }
}

class MissionManager extends EventEmitter {
  private chaptIndex = 0
  private chapts: Array<MissionChapter> = []
  private curr: string | null = null
  private objs: Record<string, MissionObject> = {}
  public template = ''
  private toolbox_: ToolboxType | null = null
  toolboxView: { spec: ToolboxType; top: number } = {
    spec: DEFAULT_TOOLBOX,
    top: 0,
  }

  get chapter(): MissionChapter | null {
    const chapt = this.chapts[this.chaptIndex]
    if (!chapt) return null
    return { ...chapt, toolbox: this.toolbox }
  }

  get chapterIndex() {
    return this.chaptIndex
  }

  set chapterIndex(index: number) {
    this.chaptIndex = Math.min(
      Math.max(0, Math.round(index)),
      this.chapts.length - 1,
    )
    this.emitChange('chapter')
  }

  get chapters(): Array<MissionChapter> {
    return this.chapts
  }

  set chapters(chapters: MissionChapterSpec[]) {
    this.chapts = chapters.map((chapt) => {
      chapt = { ...chapt }
      if (chapt.toolbox) {
        chapt.toolbox = parseToolbox(chapt.toolbox)
      }
      return chapt
    })
    this.chaptIndex = 0
    this.emitChange('chapter')
  }

  get current(): MissionObject | null {
    return (this.curr && this.objs[this.curr]) || null
  }

  set current(val: string | MissionObject | null) {
    this.curr =
      val === null ? null
      : typeof val === 'string' ? val
      : val.name
    this.emitChange('object')
    this.emitChange('chapter')
  }

  /**
   * `current`（エディタで選択中のオブジェクト）を一時的に無効化して fn を実行する。
   * 変更イベントは発火しない。
   *
   * おためしプレイ用に保存済みワークスペースを復元する際、control_clone の複製先
   * ドロップダウンは選択肢から選択中オブジェクトを除外する。全オブジェクトの
   * workspace を単一の `current` を参照しながら復元するため、保存済みの複製先が
   * たまたま選択中オブジェクトと一致すると「選択不可」と判定され VAL が落ちる。
   * 復元中だけ `current` を無効化して全オブジェクト名を選択肢に含め、保存値を
   * 取りこぼさないようにする。
   */
  runWithoutCurrentObject<T>(fn: () => T): T {
    const saved = this.curr
    this.curr = null
    try {
      return fn()
    } finally {
      this.curr = saved
    }
  }

  emitChange(additionalEvent?: 'chapter' | 'object') {
    if (additionalEvent === 'chapter') {
      this.emit(additionalEvent, this.chapter)
    } else if (additionalEvent === 'object') {
      this.emit(additionalEvent, this.current)
    }
    this.emit('change')
  }

  hasSound(name?: string) {
    return !!(name ? this.objs[name] : this.current)?.sound
  }

  protected get keys() {
    return Object.keys(this.objs)
  }

  nextChapter() {
    if (!this.chapts.length) return
    this.chaptIndex = (this.chaptIndex + 1) % this.chapts.length
    this.emitChange('chapter')
  }

  nextObject() {
    const keys = this.keys
    if (!this.curr || !keys.length) return
    const idx = keys.indexOf(this.curr)
    // This fires a change event.
    this.current = keys[(idx + 1) % keys.length]
  }

  get objects() {
    return Object.values(this.objs)
  }

  set objects(objects: MissionObject[]) {
    this.objs = Object.fromEntries(
      objects.filter(({ name }) => name).map((obj) => [obj.name, obj]),
    )
    this.curr = this.keys[0] || null
    this.emitChange('object')
  }

  get objectNames() {
    return this.keys
  }

  prevObject() {
    const keys = this.keys
    if (!this.curr || !keys.length) return
    const idx = keys.indexOf(this.curr)
    // This fires a change event.
    this.current = keys[(idx - 1 + keys.length) % keys.length]
  }

  get saveData() {
    return {
      code: missionManager.objects.reduce(
        (acc, obj) => {
          const hasValidCode = obj.code && obj.code.trim() !== ''
          acc[obj.name] = hasValidCode ? obj.code! : ''
          return acc
        },
        {} as Record<string, string>,
      ),
      workspaces: missionManager.objects.reduce(
        (acc, obj) => {
          acc[obj.name] = obj.workspace
          return acc
        },
        {} as Record<string, unknown>,
      ),
    }
  }

  serializeCurrentWorkspace(workspace: Blockly.WorkspaceSvg) {
    if (!this.current) return
    this.current.code = generateCode(workspace)
    this.current.workspace = Blockly.serialization.workspaces.save(workspace)
    this.emit('workspace')
  }

  get toolbox(): ToolboxType | null {
    const chaptTb = this.chapts[this.chaptIndex]?.toolbox
    return typeof chaptTb === 'undefined' ? this.toolbox_ : chaptTb
  }

  set toolbox(toolbox: ToolboxType | string | null) {
    this.toolbox_ = parseToolbox(toolbox)
    this.emitChange('chapter')
  }
}

export const missionManager = new MissionManager()

Blockly.Extensions.register(
  missionObjectExtensionName,
  function (this: Blockly.Block) {
    this.getInput(missionObjectFieldName)?.appendField(
      new Blockly.FieldDropdown(() =>
        missionManager.objectNames
          .filter(
            (name) =>
              name !== missionManager.current?.name ||
              missionManager.objectNames.length === 1,
          )
          .map((name) => [name, name]),
      ),
      'VAL',
    )
    this.getInput(missionObjectOrThisFieldName)?.appendField(
      new Blockly.FieldDropdown(() =>
        missionManager.objectNames.map((name) => [name, name]),
      ),
      'VAL',
    )
  },
)

export const registerMissionCallbacks = (workspace: Blockly.WorkspaceSvg) => {
  if (!missionManager.current) return
  const objectName = missionManager.current.name

  workspace.addChangeListener((event) => {
    if (missionManager.current?.name !== objectName) return
    if (
      missionManager.current &&
      ((event.type === Blockly.Events.BLOCK_CHANGE &&
        // @ts-expect-error Internal property of Blockly.Events.BlockChange.
        !DISABLED_REASONS.includes(event.disabledReason)) ||
        (
          [
            Blockly.Events.BLOCK_DELETE,
            Blockly.Events.BLOCK_MOVE,
            Blockly.Events.VAR_CREATE,
            Blockly.Events.VAR_DELETE,
            Blockly.Events.VAR_RENAME,
          ] as string[]
        ).includes(event.type))
    ) {
      disableInvalidBlocks(workspace)
      missionManager.serializeCurrentWorkspace(workspace)
    }
  })

  let lastToolbox: ToolboxType | null = null
  const setToolbox = (chapt: MissionChapter | null) => {
    if (!workspace.getToolbox() || missionManager.current?.name !== objectName)
      return

    let newToolbox: ToolboxType = DEFAULT_TOOLBOX
    try {
      if (chapt?.toolbox) newToolbox = chapt.toolbox
    } catch {
      /* noop */
    }

    // Figure out what changes we need to apply.
    const toolboxSpecChanged = !deepEquals(newToolbox, lastToolbox)
    const scrollTarget =
      deepEquals(newToolbox, missionManager.toolboxView.spec) ?
        missionManager.toolboxView.top
      : 0

    // If this object's toolbox changed, update it.
    if (toolboxSpecChanged) {
      lastToolbox = newToolbox
      missionManager.toolboxView.spec = newToolbox
      workspace.updateToolbox(
        newToolbox as Blockly.utils.toolbox.ToolboxDefinition,
      )

      // Hack. When the toolbox is updated, the blocks in the main workspace jump around.
      // Do a little scroll dance to force a redraw in the right place.
      const x = workspace.scrollX
      const y = workspace.scrollY
      workspace.scroll(x + 1, y)
      workspace.scroll(x, y)
    }

    // Update the flyout contents.
    workspace.refreshToolboxSelection()

    // Scroll to the saved position or to the top.
    setTimeout(() => {
      const flyout = workspace.getToolbox()?.getFlyout() as ContinuousFlyout
      if (flyout) {
        flyout.getWorkspace()?.scrollbar?.setY(scrollTarget)
        // Make sure the correct category is selected.
        flyout.selectCategoryByScrollPosition_?.(scrollTarget)
      }
    }, 0)
  }
  missionManager.on('chapter', setToolbox)
  setToolbox(missionManager.chapter)

  const delayedSetToolbox = (obj: MissionChapter) => {
    if (obj?.chapter?.name !== objectName) return
    setTimeout(() => {
      setToolbox(missionManager.chapter)
    }, 0)
  }
  missionManager.on('object', delayedSetToolbox)

  workspace
    .getToolbox()
    ?.getFlyout()
    ?.getWorkspace()
    .addChangeListener((event) => {
      if (event.type === Blockly.Events.VIEWPORT_CHANGE) {
        const evt = event as Blockly.Events.ViewportChange
        if (typeof evt.viewTop === 'number') {
          missionManager.toolboxView.top = evt.viewTop
          missionManager.toolboxView.spec =
            missionManager.chapter?.toolbox ?? DEFAULT_TOOLBOX
        }
      }
    })

  const setWorkspace = (obj: MissionObject | null) => {
    if (missionManager.current?.name !== objectName) return
    if (!workspace.rendered) return
    if (!obj?.workspace) {
      Blockly.serialization.workspaces.load({}, workspace)
    } else if (typeof obj.workspace === 'object') {
      Blockly.serialization.workspaces.load(obj.workspace, workspace)
    } else if (typeof obj.workspace === 'string') {
      try {
        Blockly.serialization.workspaces.load(
          JSON.parse(obj.workspace),
          workspace,
        )
      } catch {
        Blockly.Xml.domToWorkspace(
          Blockly.utils.xml.textToDom(obj.workspace),
          workspace,
        )
      }
    }
    missionManager.serializeCurrentWorkspace(workspace)
  }
  setWorkspace(missionManager.current)

  // Unregister these listeners when the BlocklyComponent is unmounted.
  return () => {
    missionManager.off('chapter', setToolbox)
    missionManager.off('object', delayedSetToolbox)
  }
}
